/**
 * Product page (PDP) interactivity.
 *
 * Handles gallery thumbnail switching, mobile scroll-snap dot sync,
 * product detail tabs/accordion, variant swatch selection, and
 * variation-specific gallery swapping.
 *
 * @package Sansegynge
 */

import '../css/main.css';

/**
 * Format a numeric price in Danish style (no decimals, "kr." suffix).
 *
 * @param {number|string} price - The price value.
 * @returns {string} Formatted price, e.g. "1.295 kr."
 */
function formatDanishPrice(price) {
	return Number(price).toLocaleString('da-DK', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	}) + ' kr.';
}

document.addEventListener('DOMContentLoaded', () => {
	initGallery();
	initVariantSwatches();
	initDetailsTabs();
	initProductAccordion();
	initQuantityButtons();
	initStickyAddToCart();
	initLightbox();
	initVideoGallery();

	// Bind variation events via jQuery ready so they integrate with
	// WooCommerce's own jQuery-based variation form initialization.
	if (typeof jQuery !== 'undefined') {
		jQuery(function () {
			initVariationUpdates();
		});
	}
});

/**
 * Crossfade the main gallery image to a new source.
 *
 * Places a new image on top of the old one and fades it in (150ms).
 * The old image stays visible underneath so the background never shows.
 *
 * @param {HTMLImageElement} mainImg - The main image element.
 * @param {Object} imageData - Image data with src, srcset, sizes, alt.
 * @param {Function} [onSwapped] - Optional callback when crossfade completes.
 */
function crossfadeImage(mainImg, imageData, onSwapped) {
	if (!mainImg || !imageData) return;

	// Skip transition if same image.
	if (mainImg.src === imageData.src) {
		if (onSwapped) onSwapped();
		return;
	}

	const container = mainImg.parentElement;
	if (!container) return;

	// Cancel any in-progress crossfade by removing old overlays.
	container.querySelectorAll('[data-crossfade-overlay]').forEach((old) => old.remove());

	// Ensure container is positioned so the overlay can be absolute.
	if (getComputedStyle(container).position === 'static') {
		container.style.position = 'relative';
	}

	// Create overlay image that fades in on top of the current one.
	const overlay = document.createElement('img');
	overlay.setAttribute('data-crossfade-overlay', '');
	overlay.src = imageData.src;
	if (imageData.srcset) overlay.srcset = imageData.srcset;
	if (imageData.sizes) overlay.sizes = imageData.sizes;
	overlay.alt = imageData.alt || '';
	overlay.className = mainImg.className;
	overlay.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;opacity:0;transition:opacity 150ms ease;pointer-events:none;z-index:1;';

	container.appendChild(overlay);

	let finalized = false;
	function finalize() {
		if (finalized) return;
		finalized = true;

		// Use decode() to ensure the browser has the image ready to paint
		// before we remove the overlay. This prevents the beige flash.
		mainImg.src = imageData.src;
		if (imageData.srcset) mainImg.srcset = imageData.srcset;
		if (imageData.sizes) mainImg.sizes = imageData.sizes;
		mainImg.alt = imageData.alt || '';

		const cleanup = () => {
			overlay.remove();
			if (onSwapped) onSwapped();
		};

		// decode() ensures the image is ready to render before we remove
		// the overlay that covers it. Falls back gracefully if unsupported.
		if (mainImg.decode) {
			mainImg.decode().then(cleanup).catch(cleanup);
		} else {
			cleanup();
		}
	}

	// Wait for overlay image to load, then fade it in.
	function startFade() {
		// Force layout so the opacity transition triggers.
		// eslint-disable-next-line no-unused-expressions
		overlay.offsetHeight;
		overlay.style.opacity = '1';
		overlay.addEventListener('transitionend', finalize, { once: true });
		// Safety fallback in case transitionend doesn't fire.
		setTimeout(finalize, 250);
	}

	if (overlay.complete) {
		startFade();
	} else {
		overlay.onload = startFade;
		// Fallback if image takes too long — just swap directly.
		setTimeout(() => {
			if (overlay.style.opacity === '0') {
				finalize();
			}
		}, 500);
	}
}

/**
 * Bind click events on desktop thumbnail buttons.
 *
 * Extracted as a helper so both initGallery and rebuildGallery can
 * share the same binding logic without duplication.
 *
 * @param {HTMLElement} gallery - The gallery root element.
 * @param {Array} images - Array of image data objects.
 */
function bindThumbClicks(gallery, images) {
	const thumbs = gallery.querySelectorAll('[data-gallery-thumb]:not([data-gallery-video])');
	const mainImg = gallery.querySelector('[data-gallery-main-img]');
	const counter = gallery.querySelector('[data-gallery-current]');

	thumbs.forEach((thumb) => {
		thumb.addEventListener('click', () => {
			const index = parseInt(thumb.dataset.galleryThumb, 10);
			if (!images[index] || !mainImg) return;

			// Crossfade to new image.
			crossfadeImage(mainImg, images[index], () => {
				// Update counter after src swap.
				if (counter) counter.textContent = index + 1;
			});

			// Update active thumbnail border immediately (no delay).
			gallery.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
				t.classList.remove('border-active');
				t.classList.add('border-subtle');
			});
			thumb.classList.remove('border-subtle');
			thumb.classList.add('border-active');
		});
	});
}

/**
 * Bind prev/next arrow navigation on the desktop gallery.
 *
 * Clones the buttons to remove stale listeners (important when
 * rebuildGallery swaps the image set for a different variation).
 *
 * @param {HTMLElement} gallery - The gallery root element.
 * @param {Array} images - Array of image data objects.
 */
function bindArrowNav(gallery, images) {
	const prevBtn = gallery.querySelector('[data-gallery-prev]');
	const nextBtn = gallery.querySelector('[data-gallery-next]');
	if (!prevBtn || !nextBtn || !images.length) return;

	function navigateTo(index) {
		// Wrap around.
		const total = images.length;
		const target = ((index % total) + total) % total;

		// Simulate thumbnail click — find the thumb and click it.
		const thumb = gallery.querySelector(`[data-gallery-thumb="${target}"]:not([data-gallery-video])`);
		if (thumb) thumb.click();
	}

	function getCurrentIndex() {
		const counter = gallery.querySelector('[data-gallery-current]');
		return counter ? parseInt(counter.textContent, 10) - 1 : 0;
	}

	// Remove old listeners by cloning (simple approach since arrows are simple buttons).
	const newPrev = prevBtn.cloneNode(true);
	const newNext = nextBtn.cloneNode(true);
	prevBtn.parentNode.replaceChild(newPrev, prevBtn);
	nextBtn.parentNode.replaceChild(newNext, nextBtn);

	newPrev.addEventListener('click', () => navigateTo(getCurrentIndex() - 1));
	newNext.addEventListener('click', () => navigateTo(getCurrentIndex() + 1));
}

/**
 * Bind mobile scroll-snap dot sync.
 *
 * @param {HTMLElement} gallery - The gallery root element.
 */
function bindScrollDots(gallery) {
	const scrollContainer = gallery.querySelector('[data-gallery-scroll]');
	const dots = gallery.querySelectorAll('[data-gallery-dot]');

	if (scrollContainer && dots.length) {
		let scrollTimer = null;

		scrollContainer.addEventListener('scroll', () => {
			if (scrollTimer) clearTimeout(scrollTimer);
			scrollTimer = setTimeout(() => {
				const scrollLeft = scrollContainer.scrollLeft;
				const itemWidth = scrollContainer.firstElementChild?.offsetWidth || 1;
				const activeIndex = Math.round(scrollLeft / itemWidth);

				dots.forEach((dot, i) => {
					if (i === activeIndex) {
						dot.classList.remove('bg-[rgba(181,160,129,0.3)]');
						dot.classList.add('bg-text-primary');
					} else {
						dot.classList.remove('bg-text-primary');
						dot.classList.add('bg-[rgba(181,160,129,0.3)]');
					}
				});
			}, 50);
		}, { passive: true });
	}
}

/**
 * Product gallery — desktop thumbnail switching + mobile scroll dots.
 */
function initGallery() {
	const gallery = document.querySelector('[data-product-gallery]');
	if (!gallery) return;

	// Parse global gallery images.
	const imagesJson = gallery.querySelector('[data-gallery-images]');
	let images = [];
	if (imagesJson) {
		try {
			images = JSON.parse(imagesJson.textContent);
		} catch (e) {
			// Silently fail — gallery still works without JS swap.
		}
	}

	// Store active images on the gallery element for lightbox sync.
	gallery.__activeImages = images;

	// Bind desktop thumbnail clicks.
	bindThumbClicks(gallery, images);

	// Bind desktop arrow navigation.
	bindArrowNav(gallery, images);

	// Bind mobile scroll dot sync.
	bindScrollDots(gallery);
}

/**
 * Visual variant swatches — sync with WooCommerce's hidden <select>.
 */
function initVariantSwatches() {
	const swatchContainers = document.querySelectorAll('[data-variant-swatches]');

	swatchContainers.forEach((container) => {
		const attributeName = container.dataset.variantSwatches;
		const selectEl = document.querySelector(`select#${attributeName}`);
		if (!selectEl) return;

		const swatches = container.querySelectorAll('[data-swatch-value]');
		const swatchLabel = document.querySelector(
			`[data-swatch-label-for="${attributeName}"]`,
		);

		function updateSwatchLabel(activeSwatch) {
			if (!swatchLabel) return;
			if (activeSwatch) {
				const label =
					activeSwatch.dataset.swatchLabel || activeSwatch.dataset.swatchValue;
				swatchLabel.textContent = `: ${label}`;
			} else {
				swatchLabel.textContent = '';
			}
		}

		swatches.forEach((swatch) => {
			swatch.addEventListener('click', () => {
				const value = swatch.dataset.swatchValue;

				// Update the hidden WooCommerce select.
				// Must use jQuery .trigger() because WooCommerce's variation JS
				// binds via jQuery delegated events which ignore vanilla DOM events.
				selectEl.value = value;
				if (typeof jQuery !== 'undefined') {
					jQuery(selectEl).trigger('change');
				} else {
					selectEl.dispatchEvent(new Event('change', { bubbles: true }));
				}

				// Update visual state.
				swatches.forEach((s) => {
					s.classList.remove('border-active', 'scale-110');
					s.classList.add('border-[rgba(181,160,129,0.3)]');
				});
				swatch.classList.remove('border-[rgba(181,160,129,0.3)]');
				swatch.classList.add('border-active', 'scale-110');

				updateSwatchLabel(swatch);
			});
		});

		// Sync visual state when WooCommerce updates the select programmatically.
		selectEl.addEventListener('change', () => {
			const currentValue = selectEl.value;
			swatches.forEach((s) => {
				if (s.dataset.swatchValue === currentValue) {
					s.classList.remove('border-[rgba(181,160,129,0.3)]');
					s.classList.add('border-active', 'scale-110');
				} else {
					s.classList.remove('border-active', 'scale-110');
					s.classList.add('border-[rgba(181,160,129,0.3)]');
				}
			});
			const activeSwatch = container.querySelector(
				`[data-swatch-value="${currentValue}"]`,
			);
			updateSwatchLabel(activeSwatch);
		});
	});
}

/**
 * Product detail tabs — desktop tab switching.
 */
function initDetailsTabs() {
	const tabContainer = document.querySelector('[data-product-tabs]');
	if (!tabContainer) return;

	const tabs = tabContainer.querySelectorAll('[data-tab-trigger]');
	const panels = tabContainer.querySelectorAll('[data-tab-panel]');

	tabs.forEach((tab) => {
		tab.addEventListener('click', () => {
			const target = tab.dataset.tabTrigger;

			// Update tab active state.
			tabs.forEach((t) => {
				t.classList.remove('border-text-primary', 'text-text-primary');
				t.classList.add('border-transparent', 'text-text-secondary');
				t.setAttribute('aria-selected', 'false');
			});
			tab.classList.remove('border-transparent', 'text-text-secondary');
			tab.classList.add('border-text-primary', 'text-text-primary');
			tab.setAttribute('aria-selected', 'true');

			// Show target panel, hide others (inline style to avoid Tailwind class conflicts).
			panels.forEach((panel) => {
				panel.style.display = panel.dataset.tabPanel === target ? '' : 'none';
			});
		});
	});
}

/**
 * Product accordion (FAQ, mobile details sections).
 *
 * Reuses the same data-attribute pattern as the footer accordion in main.js.
 */
function initProductAccordion() {
	const toggles = document.querySelectorAll('[data-product-accordion-toggle]');

	toggles.forEach((toggle) => {
		toggle.addEventListener('click', () => {
			const content = toggle
				.closest('[data-product-accordion-item]')
				?.querySelector('[data-product-accordion-content]');

			if (!content) return;

			const isOpen = toggle.getAttribute('aria-expanded') === 'true';
			toggle.setAttribute('aria-expanded', String(!isOpen));

			if (isOpen) {
				content.style.maxHeight = '0';
			} else {
				content.style.maxHeight = content.scrollHeight + 'px';
			}
		});
	});
}

/**
 * Quantity +/- buttons.
 */
function initQuantityButtons() {
	document.querySelectorAll('[data-quantity-selector]').forEach((selector) => {
		const input = selector.querySelector('input[type="number"]');
		const minus = selector.querySelector('[data-quantity-minus]');
		const plus = selector.querySelector('[data-quantity-plus]');
		if (!input || !minus || !plus) return;

		const min = parseInt(input.min, 10) || 1;
		const max = parseInt(input.max, 10) || Infinity;

		minus.addEventListener('click', () => {
			const current = parseInt(input.value, 10) || min;
			if (current > min) {
				input.value = current - 1;
				input.dispatchEvent(new Event('change', { bubbles: true }));
			}
		});

		plus.addEventListener('click', () => {
			const current = parseInt(input.value, 10) || min;
			if (current < max) {
				input.value = current + 1;
				input.dispatchEvent(new Event('change', { bubbles: true }));
			}
		});
	});
}

/**
 * Sticky mobile add-to-cart bar.
 *
 * Shows a fixed bottom bar on mobile when the main add-to-cart button
 * scrolls out of the viewport.
 */
function initStickyAddToCart() {
	const mainButton = document.querySelector('[data-add-to-cart-main]');
	const stickyBar = document.querySelector('[data-sticky-add-to-cart]');

	if (!mainButton || !stickyBar) return;

	let hasBeenSeen = false;

	const observer = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					hasBeenSeen = true;
					stickyBar.classList.add('translate-y-full');
					stickyBar.classList.remove('translate-y-0');
				} else if (hasBeenSeen) {
					// Only show sticky bar after user has scrolled past the CTA.
					stickyBar.classList.remove('translate-y-full');
					stickyBar.classList.add('translate-y-0');
				}
			});
		},
		{ threshold: 0 }
	);

	observer.observe(mainButton);

	// Sticky bar button: scroll to main CTA and click it.
	const stickyBtn = stickyBar.querySelector('[data-sticky-add-to-cart-btn]');
	if (stickyBtn) {
		stickyBtn.addEventListener('click', () => {
			mainButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
			setTimeout(() => mainButton.click(), 400);
		});
	}
}

/**
 * Dynamic variation updates for variable products.
 *
 * Listens to WooCommerce jQuery events (show_variation, hide_variation,
 * found_variation, reset_image) to update price, stock status, sticky bar
 * price, and gallery (full rebuild with thumbnails, scroll strip, dots,
 * counter, lightbox) without a page reload.
 *
 * CRITICAL: WooCommerce fires variation events via jQuery — vanilla
 * addEventListener will NOT work. Must use jQuery(form).on().
 */
function initVariationUpdates() {
	const form = document.querySelector('.variations_form');
	if (!form) return;

	// Require jQuery (WC variation events are jQuery-only).
	if (typeof jQuery === 'undefined') return;

	const $form = jQuery(form);

	// — Price (FRONT-02) —
	const priceEl = document.querySelector('[data-product-price]');
	const originalPriceHTML = priceEl ? priceEl.innerHTML : '';

	// — Sticky bar price (FRONT-03) —
	const stickyPriceEl = document.querySelector('[data-sticky-price]');
	const originalStickyPrice = stickyPriceEl ? stickyPriceEl.textContent : '';

	// — Stock status (FRONT-04) —
	const stockEl = document.querySelector('[data-stock-status]');
	const originalStockHTML = stockEl ? stockEl.innerHTML : '';

	// — Gallery (FRONT-05 + variation gallery swap) —
	const gallery = document.querySelector('[data-product-gallery]');

	// Helper: always get a fresh reference to the main image element, in case
	// it has been destroyed / recreated by innerHTML operations.
	function getMainImg() {
		return gallery ? gallery.querySelector('[data-gallery-main-img]') : null;
	}

	const mainImg = getMainImg();
	const originalImg = mainImg ? {
		src: mainImg.src,
		srcset: mainImg.srcset || '',
		sizes: mainImg.sizes || '',
		alt: mainImg.alt || '',
	} : null;

	// — Parse variation galleries JSON —
	const variationGalleriesJson = gallery ? gallery.querySelector('[data-variation-galleries]') : null;
	let variationGalleries = {};
	if (variationGalleriesJson) {
		try { variationGalleries = JSON.parse(variationGalleriesJson.textContent); } catch (e) { /* empty */ }
	}

	// — Parse original global gallery images —
	const globalImagesJson = gallery ? gallery.querySelector('[data-gallery-images]') : null;
	let originalImages = [];
	if (globalImagesJson) {
		try { originalImages = JSON.parse(globalImagesJson.textContent); } catch (e) { /* empty */ }
	}

	// — Store original gallery HTML for reset —
	const thumbContainer = gallery ? gallery.querySelector('.flex.flex-col.gap-2') : null;
	const scrollContainer = gallery ? gallery.querySelector('[data-gallery-scroll]') : null;
	const dotsContainer = gallery ? gallery.querySelector('[data-gallery-dot]')?.parentElement : null;
	const totalEl = gallery ? gallery.querySelector('[data-gallery-total]') : null;

	const originalThumbsHTML = thumbContainer ? thumbContainer.innerHTML : '';
	const originalScrollHTML = scrollContainer ? scrollContainer.innerHTML : '';
	const originalDotsHTML = dotsContainer ? dotsContainer.innerHTML : '';
	const originalTotalCount = totalEl ? totalEl.textContent : '';

	// Save a reference to the video thumbnail button (if it exists) so we
	// can re-append it after rebuilding image thumbnails.
	const videoThumbBtn = thumbContainer ? thumbContainer.querySelector('[data-gallery-video]') : null;
	const videoThumbClone = videoThumbBtn ? videoThumbBtn.cloneNode(true) : null;

	/**
	 * Rebuild the entire gallery (desktop thumbnails, main image, mobile
	 * scroll strip, dots, counter, lightbox) from a new images array.
	 *
	 * @param {Array} images - Array of image data objects.
	 */
	function rebuildGallery(images) {
		if (!gallery || !images.length) return;

		// Update shared active images for lightbox sync.
		gallery.__activeImages = images;

		// — Desktop thumbnails —
		if (thumbContainer) {
			// Clear all thumbnails.
			thumbContainer.innerHTML = '';

			images.forEach((image, index) => {
				const btn = document.createElement('button');
				btn.type = 'button';
				btn.className = 'w-[72px] aspect-square bg-surface overflow-hidden border transition-colors duration-200 cursor-pointer ' +
					(index === 0 ? 'border-active' : 'border-subtle');
				btn.setAttribute('data-gallery-thumb', index);
				btn.setAttribute('aria-label', `Vis billede ${index + 1}`);

				const img = document.createElement('img');
				img.src = image.thumb || image.src;
				img.className = 'w-full h-full object-cover';
				img.alt = '';
				img.loading = 'lazy';

				btn.appendChild(img);
				thumbContainer.appendChild(btn);
			});

			// Re-append video thumbnail if it exists.
			if (videoThumbClone) {
				thumbContainer.appendChild(videoThumbClone.cloneNode(true));
			}
		}

		// — Desktop main image (with opacity transition) —
		const currentMainImg = getMainImg();
		if (currentMainImg) {
			// Force a layout flush so the opacity-0 transition actually
			// starts before we swap the src.
			currentMainImg.style.opacity = '0';
			// eslint-disable-next-line no-unused-expressions
			currentMainImg.offsetHeight;

			currentMainImg.src = images[0].src;
			currentMainImg.srcset = images[0].srcset || '';
			currentMainImg.sizes = images[0].sizes || '';
			currentMainImg.alt = images[0].alt || '';
			currentMainImg.loading = 'eager';
			currentMainImg.onload = () => {
				currentMainImg.style.opacity = '1';
			};
			// Fallback: if the image is cached and onload doesn't fire.
			if (currentMainImg.complete) {
				currentMainImg.style.opacity = '1';
			}
		}

		// — Mobile scroll strip —
		if (scrollContainer) {
			scrollContainer.innerHTML = '';

			images.forEach((image, index) => {
				const wrapper = document.createElement('div');
				wrapper.className = 'flex-none w-full snap-center';

				const inner = document.createElement('div');
				inner.className = 'aspect-[3/4] bg-surface overflow-hidden';

				const img = document.createElement('img');
				img.src = image.src;
				img.srcset = image.srcset || '';
				img.sizes = image.sizes || '';
				img.alt = image.alt || '';
				img.className = 'w-full h-full object-cover';
				img.loading = index === 0 ? 'eager' : 'lazy';

				inner.appendChild(img);
				wrapper.appendChild(inner);
				scrollContainer.appendChild(wrapper);
			});

			scrollContainer.scrollLeft = 0;
		}

		// — Mobile dots —
		if (dotsContainer) {
			dotsContainer.innerHTML = '';

			images.forEach((_, index) => {
				const dot = document.createElement('span');
				dot.className = 'w-2 h-2 rounded-full transition-colors duration-200 ' +
					(index === 0 ? 'bg-text-primary' : 'bg-[rgba(181,160,129,0.3)]');
				dot.setAttribute('data-gallery-dot', index);
				dotsContainer.appendChild(dot);
			});
		}

		// — Counter —
		const currentEl = gallery.querySelector('[data-gallery-current]');
		if (currentEl) currentEl.textContent = '1';
		if (totalEl) totalEl.textContent = images.length;

		// — Re-bind events —
		bindThumbClicks(gallery, images);
		bindArrowNav(gallery, images);
		bindScrollDots(gallery);

		// Re-bind video thumb click if it was re-appended.
		if (videoThumbClone) {
			const newVideoBtn = thumbContainer ? thumbContainer.querySelector('[data-gallery-video]') : null;
			if (newVideoBtn) {
				rebindVideoThumb(newVideoBtn, gallery);
			}
		}
	}

	/**
	 * Re-bind video thumbnail click event after gallery rebuild.
	 *
	 * @param {HTMLElement} videoBtn - The video thumbnail button.
	 * @param {HTMLElement} galleryEl - The gallery root element.
	 */
	function rebindVideoThumb(videoBtn, galleryEl) {
		const mainContainer = galleryEl.querySelector('[data-gallery-main]');
		if (!mainContainer || !videoBtn) return;

		videoBtn.addEventListener('click', () => {
			const videoUrl = videoBtn.dataset.galleryVideo;
			let embedUrl = videoUrl;
			const ytMatch = videoUrl.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
			if (ytMatch) embedUrl = `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;
			const vimeoMatch = videoUrl.match(/vimeo\.com\/(\d+)/);
			if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`;

			mainContainer.innerHTML = `<iframe src="${embedUrl}" class="w-full h-full absolute inset-0" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;

			galleryEl.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
				t.classList.remove('border-active');
				t.classList.add('border-subtle');
			});
			videoBtn.classList.remove('border-subtle');
			videoBtn.classList.add('border-active');
		});
	}

	// — URL update: sync query parameters with selected attributes —
	function updateVariationUrl() {
		const url = new URL(window.location.href);
		const selects = form.querySelectorAll('.variations select');

		selects.forEach((select) => {
			const name = select.name; // e.g. "attribute_pa_farve"
			const value = select.value;
			if (value) {
				url.searchParams.set(name, value);
			} else {
				url.searchParams.delete(name);
			}
		});

		window.history.replaceState(null, '', url.toString());
	}

	$form.on('woocommerce_variation_has_changed', function () {
		updateVariationUrl();
	});

	// Also clear params when variations are reset.
	$form.on('reset_data', function () {
		const url = new URL(window.location.href);
		const selects = form.querySelectorAll('.variations select');
		selects.forEach((select) => {
			url.searchParams.delete(select.name);
		});
		window.history.replaceState(null, '', url.toString());
	});

	// — show_variation: update price, sticky price, stock —
	$form.on('show_variation', function (_event, variation) {
		// Price.
		if (priceEl && variation.display_price !== undefined && variation.display_price > 0) {
			priceEl.innerHTML =
				'<span class="text-text-primary text-xl font-medium">' +
				formatDanishPrice(variation.display_price) +
				'</span>' +
				'<span class="block text-xs text-text-secondary font-light mt-1">inkl. moms</span>';
		}

		// Sticky bar price.
		if (stickyPriceEl && variation.display_price !== undefined && variation.display_price > 0) {
			stickyPriceEl.textContent = formatDanishPrice(variation.display_price);
		}

		// Stock status.
		if (stockEl) {
			if (variation.is_in_stock) {
				stockEl.innerHTML =
					'<span class="w-2.5 h-2.5 rounded-full bg-success"></span>' +
					'<span class="text-sm text-text-primary font-light">P\u00e5 lager</span>' +
					'<span class="text-xs text-text-secondary font-light">\u2014 Levering 1-3 hverdage</span>';
			} else {
				stockEl.innerHTML =
					'<span class="w-2.5 h-2.5 rounded-full bg-[rgba(181,160,129,0.4)]"></span>' +
					'<span class="text-sm text-text-secondary font-light">Ikke p\u00e5 lager</span>';
			}
		}
	});

	// — hide_variation: reset all to original state —
	$form.on('hide_variation', function () {
		if (priceEl) priceEl.innerHTML = originalPriceHTML;
		if (stickyPriceEl) stickyPriceEl.textContent = originalStickyPrice;
		if (stockEl) stockEl.innerHTML = originalStockHTML;
	});

	// — found_variation: swap gallery to variation images —
	$form.on('found_variation', function (_event, variation) {
		if (!getMainImg() || !originalImg || !gallery) return;

		const varId = variation.variation_id;
		const varImages = variationGalleries[varId];

		if (varImages && varImages.length) {
			// Full custom gallery for this variation.
			rebuildGallery(varImages);
		} else if (variation.image && variation.image.src && variation.image.src.length > 1) {
			// Fallback: no custom gallery — put variation's single image first,
			// then the rest of the default gallery.
			const varSingleImage = {
				src: variation.image.src,
				srcset: variation.image.srcset || '',
				sizes: variation.image.sizes || '',
				alt: variation.image.alt || '',
				width: 0,
				height: 0,
				thumb: variation.image.thumb_src || variation.image.gallery_thumbnail_src || variation.image.src,
			};
			rebuildGallery([varSingleImage, ...originalImages.slice(1)]);
		}
	});

	// — reset_image: restore original gallery —
	$form.on('reset_image', function () {
		if (!gallery) return;

		// Restore desktop thumbnails.
		if (thumbContainer) {
			thumbContainer.innerHTML = originalThumbsHTML;
		}

		// Restore mobile scroll strip.
		if (scrollContainer) {
			scrollContainer.innerHTML = originalScrollHTML;
		}

		// Restore mobile dots.
		if (dotsContainer) {
			dotsContainer.innerHTML = originalDotsHTML;
		}

		// Restore main image.
		const resetMainImg = getMainImg();
		if (resetMainImg && originalImg) {
			resetMainImg.src = originalImg.src;
			resetMainImg.srcset = originalImg.srcset;
			resetMainImg.sizes = originalImg.sizes;
			resetMainImg.alt = originalImg.alt;
			resetMainImg.style.opacity = '1';
		}

		// Restore counter.
		const currentEl = gallery.querySelector('[data-gallery-current]');
		if (currentEl) currentEl.textContent = '1';
		if (totalEl) totalEl.textContent = originalTotalCount;

		// Restore active images for lightbox.
		gallery.__activeImages = originalImages;

		// Re-bind events.
		bindThumbClicks(gallery, originalImages);
		bindArrowNav(gallery, originalImages);
		bindScrollDots(gallery);
	});
}

/**
 * Full-screen image lightbox.
 *
 * Opens when clicking the expand icon on the main gallery image.
 * Supports keyboard navigation (Escape, ArrowLeft, ArrowRight).
 *
 * Reads images from gallery.__activeImages so it always shows the
 * currently active image set (global or variation-specific).
 */
function initLightbox() {
	const trigger = document.querySelector('[data-gallery-lightbox-trigger]');
	const gallery = document.querySelector('[data-product-gallery]');
	if (!trigger || !gallery) return;

	// Initial images from parsed JSON (already set in initGallery).
	if (!gallery.__activeImages || !gallery.__activeImages.length) {
		const imagesJson = gallery.querySelector('[data-gallery-images]');
		if (imagesJson) {
			try {
				gallery.__activeImages = JSON.parse(imagesJson.textContent);
			} catch (e) {
				return;
			}
		}
	}
	if (!gallery.__activeImages || !gallery.__activeImages.length) return;

	let currentIndex = 0;

	// Create overlay element.
	const overlay = document.createElement('div');
	overlay.className =
		'fixed inset-0 z-[9999] bg-[rgb(44,36,32)] hidden items-center justify-center';
	overlay.setAttribute('role', 'dialog');
	overlay.setAttribute('aria-modal', 'true');
	overlay.setAttribute('aria-label', 'Billedgalleri');
	overlay.setAttribute('tabindex', '-1');
	overlay.innerHTML = `
		<button data-lightbox-close class="absolute top-4 right-4 w-12 h-12 flex items-center justify-center text-bg-primary hover:text-accent transition-colors z-10 cursor-pointer" aria-label="Luk">
			<svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
		</button>
		<button data-lightbox-prev class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-bg-primary hover:text-accent transition-colors cursor-pointer" aria-label="Forrige billede">
			<svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M15 18l-6-6 6-6"/></svg>
		</button>
		<button data-lightbox-next class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-bg-primary hover:text-accent transition-colors cursor-pointer" aria-label="N\u00e6ste billede">
			<svg class="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
		</button>
		<img data-lightbox-img class="max-w-[90vw] max-h-[85vh] object-contain" src="" alt="" />
		<div data-lightbox-counter class="absolute bottom-4 left-1/2 -translate-x-1/2 text-bg-primary text-sm font-light"></div>
	`;
	document.body.appendChild(overlay);

	const lightboxImg = overlay.querySelector('[data-lightbox-img]');
	const lightboxCounter = overlay.querySelector('[data-lightbox-counter]');

	function showImage(index, skipTransition) {
		const images = gallery.__activeImages || [];
		if (!images.length) return;
		currentIndex = ((index % images.length) + images.length) % images.length;
		lightboxCounter.textContent = `${currentIndex + 1} / ${images.length}`;

		if (skipTransition) {
			lightboxImg.src = images[currentIndex].src;
			lightboxImg.alt = images[currentIndex].alt;
		} else {
			crossfadeImage(lightboxImg, images[currentIndex]);
		}
	}

	function open() {
		const images = gallery.__activeImages || [];
		if (!images.length) return;
		const activeThumb = gallery.querySelector('[data-gallery-thumb].border-active');
		currentIndex = activeThumb ? parseInt(activeThumb.dataset.galleryThumb, 10) : 0;
		if (currentIndex >= images.length) currentIndex = 0;
		overlay.classList.remove('hidden');
		overlay.classList.add('flex');
		document.body.style.overflow = 'hidden';
		showImage(currentIndex, true);
		overlay.focus();
	}

	function close() {
		overlay.classList.add('hidden');
		overlay.classList.remove('flex');
		document.body.style.overflow = '';
	}

	trigger.addEventListener('click', open);
	overlay.querySelector('[data-lightbox-close]').addEventListener('click', close);
	overlay.querySelector('[data-lightbox-prev]').addEventListener('click', () => showImage(currentIndex - 1));
	overlay.querySelector('[data-lightbox-next]').addEventListener('click', () => showImage(currentIndex + 1));

	// Close on backdrop click (not on image/buttons).
	overlay.addEventListener('click', (e) => {
		if (e.target === overlay) close();
	});

	// Keyboard navigation.
	overlay.addEventListener('keydown', (e) => {
		if (e.key === 'Escape') close();
		if (e.key === 'ArrowLeft') showImage(currentIndex - 1);
		if (e.key === 'ArrowRight') showImage(currentIndex + 1);
	});
}

/**
 * Video gallery support.
 *
 * Handles video thumbnail clicks in the gallery, swapping the main
 * image container with an iframe embed (YouTube/Vimeo).
 */
function initVideoGallery() {
	const gallery = document.querySelector('[data-product-gallery]');
	if (!gallery) return;

	const videoThumbs = gallery.querySelectorAll('[data-gallery-video]');
	const mainContainer = gallery.querySelector('[data-gallery-main]');
	if (!videoThumbs.length || !mainContainer) return;

	// Store original main image HTML so we can restore it.
	const originalHTML = mainContainer.innerHTML;

	function convertToEmbedUrl(url) {
		const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
		if (ytMatch) return `https://www.youtube-nocookie.com/embed/${ytMatch[1]}`;
		const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
		if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
		return url;
	}

	videoThumbs.forEach((thumb) => {
		thumb.addEventListener('click', () => {
			const videoUrl = thumb.dataset.galleryVideo;
			const embedUrl = convertToEmbedUrl(videoUrl);

			mainContainer.innerHTML = `<iframe src="${embedUrl}" class="w-full h-full absolute inset-0" frameborder="0" allow="autoplay; fullscreen; picture-in-picture" allowfullscreen></iframe>`;

			// Update thumbnail active states.
			gallery.querySelectorAll('[data-gallery-thumb]').forEach((t) => {
				t.classList.remove('border-active');
				t.classList.add('border-subtle');
			});
			thumb.classList.remove('border-subtle');
			thumb.classList.add('border-active');
		});
	});

	// When clicking a regular image thumb, restore the main image.
	gallery.querySelectorAll('[data-gallery-thumb]:not([data-gallery-video])').forEach((thumb) => {
		thumb.addEventListener('click', () => {
			const iframe = mainContainer.querySelector('iframe');
			if (iframe) {
				mainContainer.innerHTML = originalHTML;
			}
		});
	});
}
